import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react';
import * as styles from './recommendations-enquiry-section.style';
import { COMPONENT_IDS, PROFILE_TIERS } from '../../constants';
import { SectionWrapper } from '../index';

type RecommendationsEnquirySectionProps = {
  recommendations: React.ReactNode;
  enquiryForm: React.ReactNode;
  profileTier?: PROFILE_TIERS;
};

type ReviewsContextType = {
  hasReviews: boolean;
  setHasReviews: (value: boolean) => void;
  profileTier: PROFILE_TIERS;
  setProfileTier: (tier: PROFILE_TIERS) => void;
  reviewPageSize: number;
  reviewPageNumber: number;
};

const ReviewsContext = createContext<ReviewsContextType | undefined>(undefined);

export const useReviewsContext = (): ReviewsContextType => {
  const context = useContext(ReviewsContext);
  if (!context) {
    throw new Error('useReviewsContext must be used within a ReviewsProvider');
  }
  return context;
};

export const ReviewsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [hasReviews, setHasReviews] = useState(false);
  const [profileTier, setProfileTier] = useState<PROFILE_TIERS>(
    PROFILE_TIERS.basic,
  );

  const [reviewPageSize, setReviewPageSize] = useState(5);
  const [reviewPageNumber, setReviewPageNumber] = useState(1);

  useEffect(() => {
    setReviewPageSize(profileTier === PROFILE_TIERS.platinum ? 50 : 5);
    setReviewPageNumber(1);
  }, [profileTier]);

  const contextValue = useMemo(
    () => ({
      hasReviews,
      setHasReviews,
      profileTier,
      setProfileTier: (tier: PROFILE_TIERS) => setProfileTier(tier),
      reviewPageSize,
      reviewPageNumber,
    }),
    [hasReviews, profileTier, reviewPageSize, reviewPageNumber],
  );

  return (
    <ReviewsContext.Provider value={contextValue}>
      {children}
    </ReviewsContext.Provider>
  );
};

const RecommendationsEnquirySection = ({
  recommendations,
  enquiryForm,
  profileTier,
}: RecommendationsEnquirySectionProps): JSX.Element => {
  const { hasReviews, setProfileTier } = useReviewsContext();
  if (profileTier) {
    setProfileTier(profileTier);
  }
  return (
    <div css={styles.container(hasReviews)}>
      <SectionWrapper componentId={COMPONENT_IDS.recommendations} noStyles>
        {recommendations}
      </SectionWrapper>
      <div css={styles.sidebarWrapper}>
        <SectionWrapper componentId={COMPONENT_IDS.enquiryForm} noStyles>
          {enquiryForm}
        </SectionWrapper>
      </div>
    </div>
  );
};

export default RecommendationsEnquirySection;
